import { useEffect, useState } from 'react';
import { useUser } from '@clerk/react';
import { useGetMe, useUpdateMe, getGetMeQueryKey, getGetMyStatsQueryKey } from '@workspace/api-client-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';

// Auto-generated usernames look like "hooper_4821" — see api-server randomUsername()
const RANDOM_USERNAME_PATTERN = /^(fan|hooper|slugger|keeper|gridiron|baller|ace|captain)_\d{4}$/;

function dismissKey(userId: string) {
  return `boredfan-username-prompt-dismissed:${userId}`;
}

export function WelcomeUsernameDialog() {
  const { isSignedIn } = useUser();
  if (!isSignedIn) return null;
  return <WelcomeUsernameDialogInner />;
}

function WelcomeUsernameDialogInner() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: me } = useGetMe();
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!me) return;
    if (!RANDOM_USERNAME_PATTERN.test(me.username)) return;
    if (localStorage.getItem(dismissKey(me.id))) return;
    setUsername('');
    setOpen(true);
  }, [me?.id, me?.username]);

  const updateMe = useUpdateMe({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetMyStatsQueryKey() });
        setOpen(false);
        toast({
          title: 'Username set',
          className: 'border-primary bg-background text-primary rounded-none font-mono uppercase tracking-widest glow-primary',
        });
      },
      onError: (err: any) => {
        setError(err?.message || 'That username is not available');
      },
    },
  });

  if (!me) return null;

  const trimmed = username.trim();
  const valid = /^[a-zA-Z0-9_]{3,20}$/.test(trimmed);

  const handleSave = () => {
    if (!valid) {
      setError('3-20 characters: letters, numbers, underscores');
      return;
    }
    setError('');
    updateMe.mutate({ data: { username: trimmed } });
  };

  const handleSkip = () => {
    localStorage.setItem(dismissKey(me.id), '1');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleSkip(); }}>
      <DialogContent className="rounded-none border-primary/50 bg-background sm:max-w-md" data-testid="dialog-welcome-username">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-widest text-xl">Pick your username</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground font-mono">
          This is the name everyone sees on leaderboards and your profile. You can change it later in your profile.
        </p>
        <div>
          <Label htmlFor="welcome-username" className="font-mono uppercase tracking-widest text-muted-foreground text-xs mb-2 block">Username</Label>
          <Input
            id="welcome-username"
            value={username}
            onChange={(e) => { setUsername(e.target.value); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            placeholder={me.username}
            autoFocus
            className="rounded-none border-primary/40 focus-visible:ring-primary font-mono"
            data-testid="input-welcome-username"
          />
          {error && <p className="mt-2 text-xs text-destructive font-mono uppercase tracking-wider" data-testid="text-username-error">{error}</p>}
        </div>
        <div className="flex gap-3 justify-end">
          <Button
            variant="ghost"
            className="rounded-none font-mono uppercase tracking-widest text-muted-foreground"
            onClick={handleSkip}
            data-testid="button-skip-username"
          >
            Keep {me.username}
          </Button>
          <Button
            className="rounded-none font-bold uppercase tracking-widest glow-primary"
            onClick={handleSave}
            disabled={updateMe.isPending || !trimmed}
            data-testid="button-save-username"
          >
            {updateMe.isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
